<template>
  <form
    :id="formId"
    class="booking-form"
    :class="{ initializing: !initialized, loading }"
    @submit.prevent="submit"
  >
    <div class="tabbed accordion formcordion">
      <internal-nav
        class="tabs"
        :active-item="activeSection"
        :items="sections"
        :interactive="false"
        @select="openSection"
      ></internal-nav>
      <loader v-if="loading" :loading="loading"></loader>
      <div class="content-panes" v-if="initialized">
        <div
          class="tab-content"
          :class="{ active: activeSection === 'requested-date-and-time' }"
          data-section="requested-date-and-time"
        >
          <div class="inner">
            <div class="content">
              <div class="form-group vanilla">
                <litepicker
                  :inline="true"
                  :multi="!isRecce"
                  :range="!isRecce"
                  :months="2"
                  :disable-dates="disabledDates"
                  :min-date="minDate"
                  :button-text="buttonText"
                  @input="setDates"
                >
                </litepicker>
                <input-error
                  v-if="
                    errors['preferred_dates.from'] ||
                    errors['preferred_dates.to']
                  "
                >
                  Please select your preferred dates
                </input-error>
              </div>
              <transition name="fade" mode="out-in" appear :duration="500">
                <div class="form-group" v-if="hasDates">
                  <div v-if="!isRecce">
                    <input-error
                      v-if="
                        errors.call_times_arrivals ||
                        errors.call_times_departures
                      "
                    >
                      <p class="mb-6">
                        Please ensure all booking dates have been provided for
                        your preferred dates.
                      </p>
                    </input-error>
                    <transition
                      name="fade"
                      mode="out-in"
                      appear
                      :duration="500"
                    >
                      <div
                        v-if="
                          booking.preferred_dates.from !==
                          booking.preferred_dates.to
                        "
                      >
                        <div
                          class="
                            form-group
                            vanilla
                            checkbox-container
                            no-divider
                            mb-6
                          "
                        >

                        <!-- Making the text above the dates text box  -->
                        <h3 class="wt-bold mb-4">
                          {{ dateRangeLabel }}
                        </h3>
                          <checkbox
                            v-model="booking.same_call_times_for_all_dates"
                          >
                            <template v-slot:checkbox-label>
                              Bump in / Bump out times are the same for all dates
                            </template>
                          </checkbox>
                        </div>
                      </div>
                    </transition>


                    <div
                      class="form-section unlined"
                      :class="{
                        'columned two-column':
                          !booking.same_call_times_for_all_dates,
                      }"
                    >
                      <div
                        class="form-group vanilla"
                        v-for="(date, index) in dateRange"
                        :key = index
                        :class="{
                          'has-input-error':
                            errors[`call_times_arrivals.${index}`] ||
                            errors[`call_times_departures.${index}`],
                        }"
                      >
                        <h3 class="fg-primary wt-bold">

                          <template v-if="!booking.same_call_times_for_all_dates">
                            {{
                              `Day ${index + 1} - ${date.format(
                                "dddd D MMMM YYYY"
                              )}`
                            }}
                          </template>
                        </h3>
                        <div class="form-section unlined columned two-column">
                          <div class="form-group vanilla">
                            <label :for="`arrival-time_${index}`"
                              >Bump in</label
                            >
                            <select-wrapper
                              :id="`arrival-time_${index}`"
                              :options="arrivalTimeSelectOptions"
                              v-model="booking.call_times_arrivals[index]"
                            ></select-wrapper>
                            <input-error
                              v-if="errors[`call_times_arrivals.${index}`]"
                            >
                              {{ errors[`call_times_arrivals.${index}`][0] }}
                            </input-error>
                          </div>
                          <div class="form-group vanilla">
                            <label :for="`departure-time_${index}`"
                              >Bump out</label
                            >
                            <select-wrapper
                              :id="`departure-time_${index}`"
                              :options="departureTimeSelectOptions"
                              v-model="booking.call_times_departures[index]"
                            ></select-wrapper>
                            <input-error
                              v-if="errors[`call_times_departures.${index}`]"
                            >
                              {{ errors[`call_times_departures.${index}`][0] }}
                            </input-error>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <h3
                      class="booking-title-small"
                      :class="{ 'fg-red': errors.recce_arrival_time }"
                    >
                      Preferred Recce Time
                    </h3>
                    <div class="form-section unlined">
                      <div
                        class="form-group vanilla"
                        :class="{
                          'has-input-error': errors.recce_arrival_time,
                        }"
                      >
                        <label class="booking-caption-medium">
                          {{ dateRange[0].format("dddd D MMMM YYYY") }}
                        </label>
                        <select-wrapper
                          id="arrival-time_0"
                          class="booking-select-wrapper"
                          :options="arrivalTimeSelectOptions"
                          v-model="booking.recce_arrival_time"
                        ></select-wrapper>
                        <input-error v-if="errors.recce_arrival_time">
                          {{ errors.recce_arrival_time[0] }}
                        </input-error>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
              <div class="form-group submit no-divider align-right">
                <button
                  type="button"
                  class="button outline-black"
                  :class="{
                    'recce-step-1-next-btn-gtm': isRecce,
                    'booking-step-1-next-btn-gtm': !isRecce,
                  }"
                  @click="openSectionIfValid({ id: 'details-of-booking' }, true)"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          class="tab-content"
          :class="{ active: activeSection === 'details-of-booking' }"
          data-section="details-of-booking"
        >
          <div class="inner">
            <div class="content">
              <div class="form-section unlined columned two-column" style="padding-top: 20px">
                <div
                  class="form-group vanilla"
                  :class="{ 'has-input-error': errors.brand }"
                >
                  <label>
                    Brand
                    <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    v-model="booking.brand"
                    @keydown="delete errors.brand"
                  />
                  <label class="booking-caption-medium">Please add the brand you will be shooting for.</label>
                  <input-error v-if="errors.brand">{{
                    errors.brand[0]
                  }}</input-error>
                </div>
                <div
                  class="form-group vanilla"
                  :class="{ 'has-input-error': errors.campaign_title }"
                >
                  <label>
                    Campaign / Event Title
                    <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    v-model="booking.campaign_title"
                    @keydown="delete errors.campaign_title"
                  />
                  <label class="booking-caption-medium">What is the title of the campaign you are shooting?</label>
                  <input-error v-if="errors.campaign_title">{{
                    errors.campaign_title[0]
                  }}</input-error>
                </div>
              </div>

              <div class="booking-grid-two-col">
                <div class="form-section unlined mb-4">
                  <div
                    class="form-group vanilla"
                    v-if="meta.availability_types.length"
                    :class="{ 'has-input-error': errors.production_type }"
                  >
                    <label>
                      Production type
                      <span class="required">*</span>
                    </label>
                    <select-wrapper
                      id="booking-production-type"
                      class="booking-select-wrapper"
                      :options="meta.availability_types"
                      v-model="booking.production_type"
                      @input = " delete errors.production_type"
                    ></select-wrapper>
                    <input-error v-if="errors.production_type">
                      {{ errors.production_type[0] }}
                    </input-error>
                  </div>
                </div>
                <div
                  class="form-group vanilla no-divider no-padding-bottom"
                  :class="{ 'has-input-error': errors.areas_required }"
                >
                  <label>
                    Areas required
                    <span class="required">*</span>
                  </label>
                  <select-wrapper
                    id="booking-areas-required"
                    :options="areasRequiredOptions"
                    v-model="booking.areas_required"
                    @input = " delete errors.areas_required"

                  ></select-wrapper>
                  <input-error v-if="errors.areas_required">
                    {{ errors.areas_required[0] }}
                  </input-error>
                </div>
              </div>

              <div 
                class="form-group vanilla" 
                style="padding-bottom: 0px"
                :class="{ 'has-input-error': errors.description }"
              >
                <span class="label-like wt-sb" v-if="booking.production_type && meta.availability_types.find(x => x.id === booking.production_type).label === 'Events / Activations'">
                  Event Plan
                  <span class="required">*</span>
                </span>
                <span class="label-like wt-sb" v-else>
                  Production Description
                  <span class="required">*</span>
                </span>
                <div class="mt-1">
                  <textarea
                    v-model="booking.description"
                    @keydown="delete errors.description"
                    rows="2"
                  />
                  <label class="booking-caption-medium" v-if="booking.production_type && meta.availability_types.find(x => x.id === booking.production_type).label === 'Events / Activations'">
                    Describe here the format and sequencing of the event / activation including the rotation of guests throughout the hire.
                  </label>
                  <label class="booking-caption-medium" v-else>
                    Please detail clearly how many locations and which spaces you are shooting, usage, where you will want to shoot in larger rooms, how many crew and cameras, furniture moves etc. Also, parking / requirements for access.
                  </label>
                  <input-error v-if="errors.description">{{
                    errors.description[0]
                  }}</input-error>
                </div>
              </div>

              <div class="form-section unlined columned two-column" style="padding-top: 0px" v-if="isRecce">
                <div
                  class="form-group vanilla no-divider no-padding-bottom"
                  :class="{ 'has-input-error': errors.capacity }"
                >
                  <label for="booking-number-of-people">
                    People on Site
                    <span class="required">*</span>
                  </label>
                  <select-wrapper
                    :options="meta.capacities"
                    v-model="booking.capacity"
                    @input="delete errors.capacity"
                  ></select-wrapper>
                  <label class="booking-caption-medium">Total number of people on site must include crew, cast, event team, guests or anyone else.</label>
                  <input-error v-if="errors.capacity">{{
                    errors.capacity[0]
                  }}</input-error>
                </div>
                <div
                  class="form-group vanilla"
                  :class="{ 'has-input-error': errors.budget }"
                >
                  <label>
                    Budget Per Day ($) - ex GST
                    <span class="required">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    v-model="booking.budget"
                    @keydown="delete errors.budget"
                  />
                  <label class="booking-caption-medium">Enter $ amount.</label>
                  <input-error v-if="errors.budget">{{
                    errors.budget[0]
                  }}</input-error>
                </div>
              </div>

              <div class="booking-grid-two-col" style="padding-top: 0px">
                <div
                  class="form-group vanilla no-divider no-padding-bottom"
                  :class="{ 'has-input-error': errors.capacity }"
                  v-if="!isRecce"
                >
                  <label for="booking-number-of-people">
                    People on Site
                    <span class="required">*</span>
                  </label>
                  <select-wrapper
                    :options="meta.capacities"
                    v-model="booking.capacity"
                    @input="delete errors.capacity"
                  ></select-wrapper>
                  <label class="booking-caption-medium">Total number of people on site must include crew, cast, event team, guests or anyone else.</label>
                  <input-error v-if="errors.capacity">{{
                    errors.capacity[0]
                  }}</input-error>
                </div>
              </div>

              <div class="form-section unlined" v-if="!isRecce">
                <div
                  class="form-group vanilla"
                  v-if="meta.additional_services.length"
                  :class="{ 'has-input-error': errors.additional_services }"
                >
                  <label class="checkbox-group-heading"
                    >Additional Services</label
                  >
                  <div class="checkbox-group columns-3">
                    <div
                      class="column"
                      v-for="option in meta.additional_services"
                      :key="option.id"
                    >
                      <checkbox
                        :label="
                          option.is_mandatory
                            ? `${option.label} (Mandatory)`
                            : option.label
                        "
                        :input-value="option.id"
                        :value="option.id"
                        v-model="booking.additional_services"
                        :required="!!option.is_mandatory"
                        :checked="!!option.is_mandatory"
                        :disabled="!!option.is_mandatory"
                      ></checkbox>
                    </div>
                  </div>
                  <input-error v-if="errors.additional_services">
                    {{ errors.additional_services[0] }}
                  </input-error>
                </div>
              </div>

              <div class="form-group submit no-divider align-center">
                <div class="multi-action">
                  <div class="form-action align-left">
                    <button
                      type="button"
                      class="button outline-black"
                      @click="
                        openSection({ id: 'requested-date-and-time' }, true)
                      "
                    >
                      <span>Back</span>
                    </button>
                  </div>
                  <div class="form-action align-right">
                    <button
                      type="button"
                      class="button outline-black"
                      :class="{
                        'recce-step-2-next-btn-gtm': isRecce,
                        'booking-step-2-next-btn-gtm': !isRecce,
                      }"
                      @click="
                        authed
                          ? openSectionIfValid({
                              id: 'special-requirements-and-submit',
                            }, true)
                          : openSectionIfValid({ id: 'contact-details' }, true)
                      "
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="tab-content"
          :class="{ active: activeSection === 'contact-details' }"
          data-section="contact-details"
          v-if="!authed"
        >
          <div class="inner">
            <div class="content has-fixed-submit">
              <div class="intro flex">
                <div class="message flex-1 align-center">
                  <p>
                    If you're an existing user of A Perfect Space, skip this
                    step by
                    <a class="pink-link" @click="showLogin">clicking here</a> to
                    log in.
                  </p>
                </div>
              </div>
              <div class="form-section unlined columned two-column">
                <div
                  class="form-group vanilla"
                  :class="{ 'has-input-error': errors.first_name }"
                >
                  <label for="booking-first-name">
                    First name
                    <span class="required">*</span>
                  </label>
                  <input
                    id="booking-first-name"
                    type="text"
                    v-model="booking.first_name"
                  />
                  <input-error v-if="errors.first_name">
                    {{ errors.first_name[0] }}
                  </input-error>
                </div>
                <div
                  class="form-group vanilla"
                  :class="{ 'has-input-error': errors.last_name }"
                >
                  <label for="booking-last-name">
                    Last name
                    <span class="required">*</span>
                  </label>
                  <input
                    id="booking-last-name"
                    type="text"
                    v-model="booking.last_name"
                  />
                  <input-error v-if="errors.last_name">
                    {{ errors.last_name[0] }}
                  </input-error>
                </div>
                <div
                  class="form-group vanilla"
                  :class="{ 'has-input-error': errors.phone }"
                >
                  <label for="booking-phone">
                    Contact number
                    <span class="required">*</span>
                  </label>
                  <input
                    id="booking-phone"
                    type="text"
                    v-model="booking.phone"
                  />
                  <input-error v-if="errors.phone">
                    {{ errors.phone[0] }}
                  </input-error>
                </div>
                <div
                  class="form-group vanilla"
                  :class="{ 'has-input-error': errors.email }"
                >
                  <label for="booking-email">
                    Contact email
                    <span class="required">*</span>
                  </label>
                  <input
                    id="booking-email"
                    type="email"
                    v-model="booking.email"
                  />
                  <input-error v-if="errors.email">
                    {{ errors.email[0] }}
                  </input-error>
                </div>

                <transition name="fade" mode="out-in" appear :duration="250">
                  <div class="form-section columned two-column unlined">
                    <div
                      class="form-group vanilla"
                      :class="{ 'has-input-error': errors.company_name }"
                    >
                      <label for="company-name">
                        Company name
                        <span class="required">*</span>
                      </label>
                      <input
                        id="company-name"
                        type="text"
                        name="company-name"
                        v-model="booking.company_name"
                      />
                      <input-error v-if="errors.company_name">
                        {{ errors.company_name[0] }}
                      </input-error>
                    </div>
                    <div
                      class="form-group vanilla"
                      :class="{ 'has-input-error': errors.company_type }"
                    >
                      <label>
                        Company type
                        <span class="required">*</span>
                      </label>
                      <select-wrapper
                        id="company"
                        name="company"
                        :options="meta.company_types"
                        v-model="booking.company_type"
                      ></select-wrapper>
                      <input-error v-if="errors.company_type">
                        {{ errors.company_type[0] }}
                      </input-error>
                    </div>
                  </div>
                </transition>
              </div>
              <div class="form-group submit no-divider align-center">
                <div class="multi-action">
                  <div class="form-action align-left">
                    <button
                      type="button"
                      class="button outline-black"
                      @click="openSection({ id: 'details-of-booking' }, true)"
                    >
                      <span>Back</span>
                    </button>
                  </div>
                  <div class="form-action align-right">
                    <button
                      type="button"
                      class="button outline-black"
                      @click="
                        openSection(
                          { id: 'special-requirements-and-submit' },
                          true
                        )
                      "
                    >
                      Next step
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="tab-content"
          :class="{
            active: activeSection === 'special-requirements-and-submit',
          }"
          data-section="special-requirements-and-submit"
        >
          <div class="inner">
            <div class="content">
              <table class="summary-table">
                <tr>
                  <td v-if="!isRecce">Production Dates</td>
                  <td v-else>Preferred Recce Time</td>
                  <td>{{ dateRangeLabel }}</td>
                </tr>
                <tr v-if="!isRecce">
                  <td>Bump in and Out times</td>
                  <td>{{ callTimesLabel }}</td>
                </tr>
                <tr v-else>
                  <td>Arrival Time</td>
                  <td>{{ booking.recce_arrival_time.toUpperCase() }}</td>
                </tr>
                <tr>
                  <td>Brand</td>
                  <td>{{ booking.brand }}</td>
                </tr>
                <tr>
                  <td>Campaign / Event Title</td>
                  <td>{{ booking.campaign_title }}</td>
                </tr>
                <tr>
                  <td>Production type</td>
                  <td>{{ productionTypeLabel }}</td>
                </tr>
                <tr>
                  <td>Areas required</td>
                  <td>{{ areasRequiredLabel }}</td>
                </tr>
                <tr v-if="booking.production_type && meta.availability_types.find(x => x.id === booking.production_type).label === 'Events / Activations'">
                  <td>Event Plan</td>
                  <td>{{ booking.description }}</td>
                </tr>
                <tr v-else>
                  <td>Production Description</td>
                  <td>{{ booking.description }}</td>
                </tr>
                <tr v-if="booking.capacity">
                  <td>People on Site</td>
                  <td>{{ meta.capacities.find(x => x.id === booking.capacity).label }}</td>
                </tr>
                <tr v-if="!isRecce">
                  <td>Additional Services</td>
                  <td>
                    {{ additionalServicesLabel }}
                  </td>
                </tr>
                <tr v-if="isRecce">
                  <td>Budget Per Day ($) - ex GST</td>
                  <td>{{ booking.budget }}</td>
                </tr>
              </table>
              <div
                class="form-group vanilla"
                :class="{ 'has-input-error': errors.notes }"
              >
                <label for="booking-notes">
                  Any other special requirements or considerations for our listers
                </label
                >
                <textarea
                  id="booking-notes"
                  class="slim"
                  v-model="booking.notes"
                  placeholder="Enter here"
                  rows="3"
                ></textarea>
                <div v-if="!isRecce" class="mt-8 booking-notice-container">
                  <div class="booking-notice">
                    Please review your production details as it relates to the quote you have requested prior to us seeking approval from our lister.
                  </div>
                </div>
                <div v-if="isRecce" class="mt-8 booking-notice-container">
                  <div class="booking-notice">
                    Please review your production details as it relates to the recce you have requested prior to us seeking approval form our lister.
                  </div>
                </div>
              </div>
              <div
                class="form-group vanilla checkbox-container no-divider"
                :class="{ 'has-input-error': errors.agree_terms }"
              >

              <div class="mb-4 acknowlegement">
                <checkbox v-if="isRecce" v-model="booking.agree_recce_contact_details" :required="true">
                  <template v-slot:checkbox-label>
                    I acknowledge that the recce is not booked and that I will receive the address and contact details 24 hours prior to the recce start time.
                    <span class="required">*</span>
                  </template>
                </checkbox>
                <input-error v-if="errors.agree_recce_contact_details">
                  {{ errors.agree_recce_contact_details[0] }}
                </input-error>
              </div>



                <checkbox v-model="booking.agree_terms" :required="true">
                  <template v-slot:checkbox-label>
                    I agree to the hirer
                    <a
                      href="/terms-and-conditions/hirer"
                      @click.stop.prevent="showHirerTermsModal"
                    >
                      terms &amp; conditions
                    </a>
                    <span class="required">*</span>
                  </template>
                </checkbox>
                <input-error v-if="errors.agree_terms">
                  {{ errors.agree_terms[0] }}
                </input-error>
              </div>
              <div class="form-group submit no-divider align-center">
                <div class="multi-action">
                  <div class="form-action align-left">
                    <button
                      type="button"
                      class="button outline-black"
                      @click="
                        openSection(
                          {
                            id: authed
                              ? 'details-of-booking'
                              : 'contact-details',
                          },
                          true
                        )
                      "
                    >
                      <span>Back</span>
                    </button>
                  </div>
                  <div class="form-action align-right">
                    <loader-button
                      type="submit"
                      :loading="loading"
                      label="Submit"
                      :class="{
                        'new-recce-submit-btn-gtm': isRecce,
                        'new-booking-submit-btn-gtm': !isRecce,
                      }"
                    ></loader-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div style="display: none" v-if="$root.$data.honeypot">
      <input
        type="text"
        v-model="booking[$root.$data.honeypot.nameFieldName]"
        :name="$root.$data.honeypot.nameFieldName"
        :id="$root.$data.honeypot.nameFieldName"
      />
      <input
        type="text"
        v-model="booking[$root.$data.honeypot.validFromFieldName]"
        :name="$root.$data.honeypot.validFromFieldName"
      />
    </div>
  </form>
</template>

<script>
import dayjs from "dayjs";

import BaseModal from "../Base";

dayjs.extend(require("dayjs/plugin/duration"));

const customParseFormat = require("dayjs/plugin/customParseFormat");
dayjs.extend(customParseFormat);

export default {
  extends: BaseModal,
  props: {
    data: {
      type: Object,
      required: false,
    },
  },
  mounted() {
    this.loading = true;
    axios
      .get(`/api/form-meta/booking/${this.data.listing.id}`)
      .then((response) => {
        this.meta = response.data;
        this.initialized = true;
      })
      .catch(() => {})
      .then(() => (this.loading = false));
  },
  data() {
    return {
      meta: {
        unavailability_dates: [],
        availability_types: [],
        areas_required_options: {},
        additional_services: [],
      },
      booking: {
        type: this.data.type,
        listing_id: this.data.listing.id,
        preferred_dates: {
          from: "",
          to: "",
        },
        same_call_times_for_all_dates: true,
        call_times_arrivals: [],
        call_times_departures: [],
        recce_arrival_time: "am",
        first_name: null,
        last_name: null,
        phone: null,
        email: null,
        company: 0,
        company_name: null,
        company_type: null,
        password: null,
        password_confirmation: null,
        areas_required: null,
        brand: null,
        campaign_title: null,
        capacity: null,
        description: null,
        additional_services: [],
        production_type: null,
        notes: null,
        agree_terms: false,
        agree_recce_contact_details: false,
        pageUri: "",
        pageName: "",
      },
      initialized: false,
      activeSection: "requested-date-and-time",
      loading: false,
      errors: {},
      buttonText: {
        'previousMonth': '<svg version="1.0" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 1280.000000 1280.000000" preserveAspectRatio="xMidYMid meet"><g transform="translate(0.000000,1280.000000) scale(0.100000,-0.100000)" fill="#000000" stroke="none"><path d="M6155 12034 c-38 -2 -153 -11 -255 -19 -1095 -91 -2156 -517 -3025 -1214 -1117 -895 -1849 -2173 -2054 -3586 -43 -291 -54 -466 -54 -815 0 -349 12 -527 54 -815 333 -2289 2038 -4147 4289 -4675 427 -100 822 -144 1290 -144 493 0 882 46 1345 159 2252 548 3948 2441 4244 4737 37 285 45 419 45 748 -1 338 -13 521 -55 805 -189 1303 -826 2494 -1805 3375 -913 821 -2049 1316 -3271 1425 -166 15 -612 26 -748 19z m525 -614 c551 -34 1066 -147 1574 -346 174 -68 541 -248 697 -341 388 -233 674 -457 1000 -782 421 -421 701 -811 959 -1336 283 -575 440 -1135 501 -1795 17 -174 17 -666 0 -840 -60 -645 -215 -1209 -485 -1765 -484 -996 -1276 -1804 -2256 -2300 -567 -286 -1133 -451 -1795 -521 -222 -24 -728 -24 -950 0 -637 68 -1168 217 -1710 480 -386 187 -704 393 -1045 675 -148 122 -499 473 -621 621 -678 820 -1062 1750 -1160 2810 -17 174 -16 666 0 840 61 660 218 1220 501 1795 259 526 568 954 987 1366 306 301 602 530 973 752 155 93 523 273 697 341 685 269 1423 389 2133 346z"/><path d="M6000 8274 c-47 -21 -170 -140 -886 -857 -665 -665 -836 -842 -855 -882 -19 -41 -24 -66 -24 -135 0 -69 5 -94 24 -135 19 -40 190 -217 855 -882 926 -927 875 -883 1012 -883 89 0 151 21 208 71 66 58 101 136 101 224 0 138 26 106 -604 738 l-566 567 1540 0 1540 0 50 24 c61 29 117 84 147 145 33 66 33 196 0 262 -30 61 -86 116 -147 145 l-50 24 -1540 0 -1540 0 566 568 c630 631 604 599 604 737 0 56 -5 81 -28 127 -34 69 -101 131 -164 152 -73 25 -177 21 -243 -10z"/></g></svg>',
        'nextMonth': '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 2.5625C6.78781 2.5625 2.5625 6.78781 2.5625 12C2.5625 17.2122 6.78781 21.4375 12 21.4375C17.2122 21.4375 21.4375 17.2122 21.4375 12C21.4375 6.78781 17.2122 2.5625 12 2.5625ZM1.4375 12C1.4375 6.16649 6.16649 1.4375 12 1.4375C17.8335 1.4375 22.5625 6.16649 22.5625 12C22.5625 17.8335 17.8335 22.5625 12 22.5625C6.16649 22.5625 1.4375 17.8335 1.4375 12Z" fill="#18181B"/><path fill-rule="evenodd" clip-rule="evenodd" d="M7.9375 12C7.9375 11.6893 8.18934 11.4375 8.5 11.4375H14.5C14.8107 11.4375 15.0625 11.6893 15.0625 12C15.0625 12.3107 14.8107 12.5625 14.5 12.5625H8.5C8.18934 12.5625 7.9375 12.3107 7.9375 12Z" fill="#18181B"/><path fill-rule="evenodd" clip-rule="evenodd" d="M12.1023 8.60225C12.3219 8.38258 12.6781 8.38258 12.8977 8.60225L15.8977 11.6023C16.1174 11.8219 16.1174 12.1781 15.8977 12.3977L12.8977 15.3977C12.6781 15.6174 12.3219 15.6174 12.1023 15.3977C11.8826 15.1781 11.8826 14.8219 12.1023 14.6023L14.7045 12L12.1023 9.39775C11.8826 9.17808 11.8826 8.82192 12.1023 8.60225Z" fill="#18181B"/></svg>'
      }
    };
  },
  methods: {
    mayBeMakeLocationManagerMadatory() {
      const locationManager = this.meta.additional_services.filter(addService => {
        if (addService.slug == "location-manager") {
               return addService;
        }
      }).shift();
      if (locationManager == undefined) {
        return;
      }
    },
    showHirerTermsModal() {
      window.open("/pdf/hirer-agreement.pdf", '_blank');
    },
    setDates(input) {
      delete  this.errors['preferred_dates.from']
      if (typeof input === "object") {
        this.booking.preferred_dates = input;
      } else {
        this.booking.preferred_dates = {
          from: input,
          to: input,
        };
      }
    },
    submit() {
      this.loading = true;
      this.errors = {};
      if (this.booking.company > 0) {
        this.booking.company_name = null;
      }
      axios
        .post("/api/bookings/request", this.booking)
        .then((response) => {
          this.confirmAction();
          const data = response.data;
          this.$store
            .dispatch("modals/show", {
              title: `Thanks ${data.hirer_name}`,
              type: "listing-book-confirm",
              narrow: true,
              data,
            })
            .then(() => {})
            .catch(() => {});

          if (typeof response.data.auth !== "undefined" && response.data.auth) {
            // Save our user details
            this.$store.commit("user/token", response.data.auth.token);
            this.$store.dispatch("user/setAuthHeader");
            this.$store.commit("user/authenticated", response.data.auth.user);

            // Set first login/should complete profile (open welcome account panel view)
            this.$store.commit("user/setShouldCompleteProfile", true);
          }

          if (this.isRecce) {
            window.dataLayer.push({event: 'recce-submit'});

            this.$store.dispatch("intercom/track", {
              type: "Book a Recce",
            });
            console.log('Intercom New: Book a Recce');
          } else {
            window.dataLayer.push({event: 'booking-submit'});

            this.$store.dispatch("intercom/track", {
              type: "Make a Booking",
            });
            console.log('Intercom New: Make a Booking');
          }

          this.$store.dispatch("intercom/track", {
            type: this.typeLabel + " Request Submitted",
            meta: {
              listingId: this.data.listing.id,
              name: this.data.listing.space_name,
            },
          });
        })
        .catch((error) => {
          this.errors = error.response.data.errors || {};
          if (error.response.status === 422) {
            this.$toasted.show(
              `Your ${this.data.type} was unable to be submitted, please review errors and try again`,
              {
                type: "error",
              }
            );
            return this.openSectionWithErrors();
          }

          this.$toasted.show(
            "An error has occurred, please reload the page and try again",
            {
              type: "error",
            }
          );
        })
        .then(() => {
          this.loading = false;
        });
    },
    openSectionWithErrors() {
      this.$nextTick(() => {
        let errorNodes = this.$el.querySelectorAll(".input-error");

        if (errorNodes[0]) {
          let tab = errorNodes[0].closest("[data-section]");

          if (tab) {
            this.openSection(
              { id: tab.attributes["data-section"].value },
              true
            );

            // Scroll to the clicked element
            let scrollTarget = this.$el.closest(".simplebar-content-wrapper");

            if (scrollTarget) {
              setTimeout(() => {
                scrollTarget.scrollTo({
                  top:
                    $(errorNodes[0]).offset().top -
                    $(scrollTarget).offset().top -
                    100,
                  behavior: "smooth",
                });
              }, 50);
            }
          }
        }
      });
    },

    openSectionIfValid(which, scroll){
      var hasError = false;

      switch (which.id) {
        case 'details-of-booking':
          if ((this.booking.type == 'recce'  && (this.booking.preferred_dates.from == ''))
            || (this.booking.type == 'booking'  && (this.booking.preferred_dates.from == '' || this.booking.preferred_dates.to == ''))
          ){

            var err = {'preferred_dates.from':'Please select the preferred date'};

            this.errors = {...this.errors, ...err };
            hasError = true;
          }

          break;
        case 'special-requirements-and-submit':
          if (this.booking.brand == null) {
              var err = {'brand':['The brand field is required']};
              this.errors = {...this.errors, ...err };
              hasError = true;
          }

          if (this.booking.campaign_title == null ) {
              var err = {'campaign_title':['The campaign / event outline is required.']};
              this.errors = {...this.errors, ...err };
              hasError = true;
          }

          if (this.booking.description == null ) {
              if (this.booking.production_type && this.meta.availability_types.find(x => x.id === this.booking.production_type).label === 'Events / Activations') {
                var err = {'description':['The event plan field is required.']};
              } else {
                var err = {'description':['The production description field is required.']};
              }
              this.errors = {...this.errors, ...err };
              hasError = true;
          }

          if (this.booking.areas_required == null) {
            var err = {'areas_required':['The areas field is required']};
            this.errors = {...this.errors, ...err };
            hasError = true;
          }

          if (this.booking.capacity == null) {
            var err = {'capacity':['The people on site field is required']};
            this.errors = {...this.errors, ...err };
            hasError = true;
          }

          if (this.booking.type == 'recce')
          {
            if (this.booking.budget == null ) {
                var err = {'budget':['The budget is required.']};
                this.errors = {...this.errors, ...err };
                hasError = true;
            }

            if (this.booking.production_type == null) {
               var err = {'production_type':['The production type field is required']};
               this.errors = {...this.errors, ...err };
               hasError = true;
            }
          }

          if (this.booking.type == 'booking')
          {
            if (this.booking.production_type == null) {
               var err = {'production_type':['The production type field is required']};
               this.errors = {...this.errors, ...err };
               hasError = true;
            }
          }

          break;
        default:
          break;
      }


      if (!hasError){
        this.openSection(which, scroll);
      }
    },
    openSection(which, scroll) {
      this.$store.dispatch("intercom/track", {
        type: this.typeLabel + " Request Step",
        meta: {
          step: which.id,
          listingId: this.data.listing.id,
          name: this.data.listing.space_name,
        },
      });

      this.activeSection = which.id;

      if (scroll) {
        // Scroll to the clicked element
        let scrollTarget = this.$el.closest(".simplebar-content-wrapper");

        if (scrollTarget) {
          setTimeout(() => {
            scrollTarget.scrollTo({ top: 0, behavior: "smooth" });
          }, 50);
        }
      }
    },
    showLogin() {
      this.$store.commit("user/changeAuthMode", {
        which: "login",
        priority: true,
      });
    },
  },
  computed: {
    disabledDates() {

      let day = new Date()
      let tomorrow =  new Date()
      let thirdDay =  new Date()

      var todayDate = (new Date()).toISOString().split('T')[0];
      tomorrow.setDate((new Date()).getDate() + 1)
      thirdDay.setDate((new Date()).getDate() + 2)


      var tomorrowDate = tomorrow.toISOString().split('T')[0]
      var thirdDayDate = thirdDay.toISOString().split('T')[0]

      this.meta.unavailability_dates.push(todayDate)
      this.meta.unavailability_dates.push(tomorrowDate)
      this.meta.unavailability_dates.push(thirdDayDate)

      return  this.meta.unavailability_dates;
    },
    minDate() {
      return dayjs().add(1, "day").format("YYYY-MM-DD");
    },
    hasDates() {
      return (
        this.booking.preferred_dates.from !== "" &&
        this.booking.preferred_dates.to !== ""
      );
    },
    dateRange() {
      if (!this.hasDates) {
        return null;
      }

      let from = dayjs(this.booking.preferred_dates.from),
        to = dayjs(this.booking.preferred_dates.to),
        range = [];

      if (this.booking.same_call_times_for_all_dates) {
        range.push(from);
        return range;
      }

      while (from.isBefore(to) || from.isSame(to)) {
        range.push(from);
        from = from.add(1, "day");
      }

      return range;
    },
    dateRangeLabel() {
      const format = "dddd D MMMM YYYY",
        from = dayjs(this.booking.preferred_dates.from),
        to = dayjs(this.booking.preferred_dates.to);

      if (this.isRecce) {
        return from.format(format);
      }

      return `${from.format(format)} - ${to.format(format)}`;
    },
    callTimesLabel() {
      if (this.isRecce) {
        return dayjs(this.booking.recce_arrival_time, "HH:mm:ss").format(
          "h:mma"
        );
      }

      if (this.booking.same_call_times_for_all_dates) {
        const from = dayjs(
            this.booking.call_times_arrivals[0],
            "HH:mm:ss"
          ).format("h:mma"),
          to = dayjs(this.booking.call_times_departures[0], "HH:mm:ss").format(
            "h:mma"
          );

        return `${from} - ${to}`;
      }
      const datesWithTimes = this.dateRange.map((date, index) => {
        let from = dayjs(
            this.booking.call_times_arrivals[index],
            "HH:mm:ss"
          ).format("h:mma"),
          to = dayjs(
            this.booking.call_times_departures[index],
            "HH:mm:ss"
          ).format("h:mma");
        return date.format("D/M/YY") + ` (${from} - ${to})`;
      });

      return datesWithTimes.join(", ");
    },
    productionTypeLabel() {
      if (!this.booking.production_type) {
        return "";
      }
      return this.meta.availability_types.find(
        (o) => o.id === this.booking.production_type
      ).label;
    },
    areasRequiredLabel() {
      if (!this.booking.areas_required) {
        return "";
      }
      return this.meta.areas_required_options[this.booking.areas_required];
    },
    additionalServicesLabel() {
      if (this.booking.additional_services.length) {
        return this.meta.additional_services
          .filter((o) => this.booking.additional_services.includes(o.id))
          .map((o) => o.label)
          .join(", ");
      }
    },
    departureTimeSelectOptions() {
      let options = [];

      let from = dayjs().startOf("day"),
        to = dayjs().endOf("day");

      while (from.isBefore(to) || from.isSame(to)) {
        options.push({
          id: from.format("HH:mm:ss"),
          label: from.format("h:mm a"),
        });
        from = from.add(30, "minute");
      }

      return options;
    },
    arrivalTimeSelectOptions() {
      let options = [];

      if (!this.isRecce) {
        let from = dayjs().startOf("day").add(6, 'hour'),
          to = dayjs().endOf("day").add(-30, 'minute');

        while (from.isBefore(to) || from.isSame(to)) {
          options.push({
            id: from.format("HH:mm:ss"),
            label: from.format("h:mm a"),
          });
          from = from.add(30, "minute");
        }
      } else {
        options.push({
          id: 'am',
          label: 'AM'
        });
        options.push({
          id: 'pm',
          label: 'PM'
        });
      }

      return options;
    },
    areasRequiredOptions() {
      let options = [];

      if (this.meta && this.meta.areas_required_options) {
        for (let prop in this.meta.areas_required_options) {
          options.push({
            id: prop,
            label: this.meta.areas_required_options[prop],
          });
        }
      }

      return options;
    },
    isRecce() {
      return this.data.type === "recce";
    },
    typeLabel() {
      return this.isRecce ? "Recce" : "Quote";
    },
    authed() {
      return !!this.$store.getters["user/account"];
    },

    /**
     * Our form sections for our tab navigation.
     *
     * @return array
     */
    sections() {
      let sections = [
        { id: "requested-date-and-time", label: "Production Dates" },
        { id: "details-of-booking", label: "Production Details" },
        { id: "special-requirements-and-submit", label: "Quote Summary" },
      ];

      if (this.isRecce) {
        sections[0].label = "Recce Details";
        sections.splice(3, 1);
        sections[2].label = "Recce Summary";
      }

      if (!this.authed) {
        sections.splice(2, 0, {
          id: "contact-details",
          label: "Contact details",
        });
      }

      return sections;
    },
    formId() {
        return this.isRecce ? 'book-recce-form-gtm' : 'book-listing-form-gtm';
    }
  },
  watch: {
    authed(now) {
      if (now) {
        this.$store.commit("account/hide", true);
      }
    },
    dateRange: {
      handler() {
        // Pre-populate new dates in range with the 9am-5pm requested default
        this.booking.call_times_arrivals = [];
        this.booking.call_times_departures = [];

        for (let x = 0; x < this.dateRange.length; x++) {
          if (!this.booking.call_times_arrivals[x]) {
            this.booking.call_times_arrivals[x] = "09:00:00";
          }

          if (!this.booking.call_times_departures[x]) {
            this.booking.call_times_departures[x] = "17:00:00";
          }
        }
      },
      deep: true,
    },
  },

  created() {
    this.booking.pageUri = window.location.href;
    this.booking.pageName = document.title;

    this.$store.dispatch("intercom/track", {
      type: "Request " + this.typeLabel + " Opened",
      meta: {
        listingId: this.data.listing.id,
        name: this.data.listing.space_name,
      },
    });
  },
};
</script>
